import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2, User, FileCheck, Briefcase, Truck, AlertCircle, BookOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { extractErrorMessage } from '@/lib/api';
import { LedgerView } from '@/components/ledgers/LedgerView';
import type {
  Driver, DriverInput, DriverStatus, LicenseCategory,
  BloodType, DriverPayModel, DriverLanguage,
} from '@/lib/types';
import { useLegalEntities } from '@/hooks/useVehicles';
import { useOperationalProjects } from '@/hooks/useProjects';
import { IqamaInput } from '@/components/IqamaInput';
import { LanguagesSelector } from '@/components/LanguagesSelector';
import { PayModelSelector, PayPreview } from '@/components/PayModelSelector';
import { ExpiryIndicator } from '@/components/ComplianceBadge';
import { Tabs } from '@/components/Tabs';
import { EntityDocumentsPanel, DRIVER_SLOTS, DRIVER_PHOTO_SLOT } from '@/components/documents/EntityDocumentsPanel'; // PR-DOCVAULT JUN03
import { validateIqama } from '@/lib/iqama';
import { extractFieldErrors } from '@/lib/api';


/** Server field names as they appear on this form, so an error names the box
 *  the operator is looking at rather than a database column. */
const LABELS: Record<string, string> = {
  fullNameEn: 'Full name', phone: 'Phone', iqamaNumber: 'Iqama number',
  iqamaExpiry: 'Iqama expiry', licenseNumber: 'License number',
  licenseExpiry: 'License expiry', licenseCategory: 'License category',
  legalEntityId: 'Legal entity', monthlyTripTarget: 'Monthly trip target',
  bonusPerTripSar: 'Bonus per trip', endDate: 'End date', hireDate: 'Hire date',
  iban: 'IBAN', baseSalarySar: 'Base salary',
};

interface DriverFormProps {
  initial?: Driver | null;
  onSubmit: (input: DriverInput) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  /**
   * Let the form save with the usually-required fields blank.
   *
   * For a super_admin correcting an existing record. EDIT only - the create
   * schema genuinely requires these, so a relaxed create would move the refusal
   * to a 400 rather than remove it.
   */
  relaxed?: boolean;
}

const STATUSES: DriverStatus[] = ['active', 'on_leave', 'suspended', 'terminated'];
const LICENSE_CATEGORIES: LicenseCategory[] = [
  'private', 'light', 'medium', 'heavy', 'articulated', 'hazardous', 'motorcycle',
];
const BLOOD_TYPES: BloodType[] = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const blank: DriverInput = {
  fullNameEn: '',
  fullNameAr: '',
  fullNameUr: '',
  dateOfBirth: '',
  bloodType: null,
  phone: '+966',
  whatsapp: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  countryOfOrigin: '',
  languages: [],
  iqamaNumber: '',
  iqamaExpiry: '',
  licenseNumber: '',
  licenseExpiry: '',
  licenseCategory: 'heavy',
  licenseIssueDate: '',
  passportNumber: '',
  passportExpiry: '',
  passportCountry: '',
  medicalCertExpiry: '',
  legalEntityId: '',
  projectId: null,
  hireDate: '',
  endDate: '',
  payModel: 'fixed_only',
  baseSalarySar: 0,
  monthlyTripTarget: null,
  bonusPerTripSar: null,
  customPayNotes: '',
  bankName: '',
  iban: '',
  accountHolder: '',
  status: 'active',
  notes: '',
};

export function DriverForm({ initial, onSubmit, onCancel, submitLabel , relaxed}: DriverFormProps) {
  const { t } = useTranslation();
  const { data: legalEntities = [] } = useLegalEntities();
  const { data: projects = [] } = useOperationalProjects();

  const [form, setForm] = useState<DriverInput>(blank);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      setForm({
        fullNameEn: initial.fullNameEn,
        fullNameAr: initial.fullNameAr ?? '',
        fullNameUr: initial.fullNameUr ?? '',
        dateOfBirth: initial.dateOfBirth ? initial.dateOfBirth.slice(0, 10) : '',
        bloodType: initial.bloodType,
        phone: initial.phone,
        whatsapp: initial.whatsapp ?? '',
        emergencyContactName: initial.emergencyContactName ?? '',
        emergencyContactPhone: initial.emergencyContactPhone ?? '',
        countryOfOrigin: initial.countryOfOrigin ?? '',
        languages: initial.languages,
        iqamaNumber: initial.iqamaNumber,
        iqamaExpiry: initial.iqamaExpiry.slice(0, 10),
        licenseNumber: initial.licenseNumber,
        licenseExpiry: initial.licenseExpiry.slice(0, 10),
        licenseCategory: initial.licenseCategory,
        licenseIssueDate: initial.licenseIssueDate ? initial.licenseIssueDate.slice(0, 10) : '',
        passportNumber: initial.passportNumber ?? '',
        passportExpiry: initial.passportExpiry ? initial.passportExpiry.slice(0, 10) : '',
        passportCountry: initial.passportCountry ?? '',
        medicalCertExpiry: initial.medicalCertExpiry ? initial.medicalCertExpiry.slice(0, 10) : '',
        legalEntityId: initial.legalEntityId,
        projectId: (initial as Driver & { projectId?: string | null }).projectId ?? null,
        hireDate: initial.hireDate ? initial.hireDate.slice(0, 10) : '',
        endDate: initial.endDate ? initial.endDate.slice(0, 10) : '',
        payModel: initial.payModel,
        baseSalarySar: Number(initial.baseSalarySar),
        monthlyTripTarget: initial.monthlyTripTarget,
        bonusPerTripSar: initial.bonusPerTripSar != null ? Number(initial.bonusPerTripSar) : null,
        customPayNotes: initial.customPayNotes ?? '',
        bankName: initial.bankName ?? '',
        iban: initial.iban ?? '',
        accountHolder: initial.accountHolder ?? '',
        status: initial.status,
        notes: initial.notes ?? '',
      });
    } else {
      const defaultLE = legalEntities.find((le) => le.isOperational) ?? legalEntities[0];
      setForm({ ...blank, legalEntityId: defaultLE?.id ?? '' });
    }
  }, [initial, legalEntities]);

  const update = <K extends keyof DriverInput>(key: K, value: DriverInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const iqamaValidation = validateIqama(form.iqamaNumber);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!relaxed) {
      if (!form.fullNameEn?.trim()) { setError(t('drivers.errors.nameRequired')); return; }
      if (!form.phone || !/^\+\d{8,15}$/.test(form.phone)) { setError(t('drivers.errors.phoneRequired')); return; }
      if (!iqamaValidation.valid) { setError(t('drivers.errors.iqamaInvalid')); return; }
      if (!form.iqamaExpiry) { setError(t('drivers.errors.iqamaExpiryRequired')); return; }
      if (!form.licenseNumber?.trim()) { setError(t('drivers.errors.licenseRequired')); return; }
      if (!form.licenseExpiry) { setError(t('drivers.errors.licenseExpiryRequired')); return; }
      if (!form.legalEntityId) { setError(t('drivers.errors.legalEntityRequired')); return; }
    } else if (form.iqamaNumber?.trim() && !iqamaValidation.valid) {
      // Relaxed means a field may be EMPTY, not that a wrong one is acceptable.
      // A typed iqama still has to be a real iqama.
      setError(t('drivers.errors.iqamaInvalid')); return;
    }
    if (form.payModel === 'target_with_bonus') {
      if (!form.monthlyTripTarget || form.monthlyTripTarget <= 0) {
        setError(t('drivers.errors.targetRequired')); return;
      }
      if (!form.bonusPerTripSar || form.bonusPerTripSar <= 0) {
        setError(t('drivers.errors.bonusRequired')); return;
      }
    }

    // Build payload (omit empty strings, only send real values)
    const payload: DriverInput = {
      // Optional chaining throughout: without the gates above these can be
      // undefined, and .trim() on undefined throws before anything is sent.
      fullNameEn: form.fullNameEn?.trim() ?? '',
      // Same shape of rule, same treatment: the pattern allows no spaces.
      phone: form.phone?.replace(/\s/g, '') ?? '',
      // Send what validateIqama actually CHECKED. It strips spaces and dashes
      // before validating, so passing the raw field meant the form approved one
      // string and the API judged another - a pasted trailing space was enough
      // to fail /^[12][0-9]{9}$/ with the number itself perfectly correct.
      iqamaNumber: form.iqamaNumber?.replace(/[\s-]/g, ''),
      iqamaExpiry: form.iqamaExpiry,
      licenseNumber: form.licenseNumber?.trim() ?? '',
      licenseExpiry: form.licenseExpiry,
      licenseCategory: form.licenseCategory,
      legalEntityId: form.legalEntityId,
    };

    if (relaxed) {
      // OMIT blanks rather than send them. A partial update leaves an absent
      // field alone; an empty string asks the server to STORE one, which the
      // iqama pattern and the date parsers reject - so relaxing the gates
      // without this would only move the same refusal to a 400.
      const CORE = ['fullNameEn', 'phone', 'iqamaNumber', 'iqamaExpiry',
                    'licenseNumber', 'licenseExpiry', 'legalEntityId'] as const;
      for (const k of CORE) {
        const v = payload[k];
        if (v === undefined || v === null || String(v).trim() === '') delete payload[k];
      }
    }

    // PR MAY10 P4B-POLISH (A.6) — operational project assignment
    if (form.projectId !== undefined) payload.projectId = form.projectId ?? null;

    if (form.fullNameAr?.trim()) payload.fullNameAr = form.fullNameAr.trim();
    if (form.fullNameUr?.trim()) payload.fullNameUr = form.fullNameUr.trim();
    if (form.dateOfBirth?.trim()) payload.dateOfBirth = form.dateOfBirth.trim();
    if (form.bloodType) payload.bloodType = form.bloodType;
    if (form.whatsapp?.trim()) payload.whatsapp = form.whatsapp.trim();
    if (form.emergencyContactName?.trim()) payload.emergencyContactName = form.emergencyContactName.trim();
    if (form.emergencyContactPhone?.trim()) payload.emergencyContactPhone = form.emergencyContactPhone.trim();
    if (form.countryOfOrigin?.trim()) payload.countryOfOrigin = form.countryOfOrigin.trim().toUpperCase();
    if (form.languages && form.languages.length > 0) payload.languages = form.languages;
    if (form.licenseIssueDate?.trim()) payload.licenseIssueDate = form.licenseIssueDate.trim();
    if (form.passportNumber?.trim()) payload.passportNumber = form.passportNumber.trim();
    if (form.passportExpiry?.trim()) payload.passportExpiry = form.passportExpiry.trim();
    if (form.passportCountry?.trim()) payload.passportCountry = form.passportCountry.trim().toUpperCase();
    if (form.medicalCertExpiry?.trim()) payload.medicalCertExpiry = form.medicalCertExpiry.trim();
    if (form.hireDate?.trim()) payload.hireDate = form.hireDate.trim();
    if (form.endDate?.trim()) payload.endDate = form.endDate.trim();
    if (form.payModel) payload.payModel = form.payModel;
    if (form.baseSalarySar !== undefined) payload.baseSalarySar = Number(form.baseSalarySar);
    if (form.monthlyTripTarget != null) payload.monthlyTripTarget = form.monthlyTripTarget;
    if (form.bonusPerTripSar != null) payload.bonusPerTripSar = form.bonusPerTripSar;
    if (form.customPayNotes?.trim()) payload.customPayNotes = form.customPayNotes.trim();
    if (form.bankName?.trim()) payload.bankName = form.bankName.trim();
    if (form.iban?.trim()) payload.iban = form.iban.trim().toUpperCase();
    if (form.accountHolder?.trim()) payload.accountHolder = form.accountHolder.trim();
    if (form.status) payload.status = form.status;
    if (form.notes?.trim()) payload.notes = form.notes.trim();

    setSubmitting(true);
    try {
      await onSubmit(payload);
    } catch (err) {
      // The 400 carries details.fieldErrors; without this the screen printed
      // the machine's word - "invalid_input" - and discarded the sentence
      // naming the field and the rule it broke.
      const fields = extractFieldErrors(err);
      const named = Object.entries(fields)
        .map(([k, msgs]) => `${LABELS[k] ?? k}: ${msgs.join(', ')}`)
        .join(' · ');
      setError(named || extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const IdentityTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Profile photo. Stored in the document vault like every other file and
          mirrored into drivers.photo_url, which the driver list reads. Only
          available once the driver exists, since the file needs an id to hang
          off - the panel says so itself when there is none. */}
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.photo', 'Profile photo')}
        </legend>
        <div className="max-w-sm">
          <EntityDocumentsPanel scope="driver" entityId={initial?.id} slots={DRIVER_PHOTO_SLOT} />
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.name')}
        </legend>
        <Field label={t('drivers.field.fullNameEn')} required>
          <Input value={form.fullNameEn ?? ''} onChange={(e) => update('fullNameEn', e.target.value)} placeholder="Muhammad Ali" />
        </Field>
        <Field label={t('drivers.field.fullNameAr')}>
          <Input dir="rtl" value={form.fullNameAr ?? ''} onChange={(e) => update('fullNameAr', e.target.value)} placeholder="محمد علي" style={{ fontFamily: '"Noto Naskh Arabic", "Tahoma", sans-serif' }} />
        </Field>
        <Field label={t('drivers.field.fullNameUr')}>
          <Input dir="rtl" value={form.fullNameUr ?? ''} onChange={(e) => update('fullNameUr', e.target.value)} placeholder="محمد علی" style={{ fontFamily: '"Noto Nastaliq Urdu", "Tahoma", sans-serif' }} />
        </Field>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.contact')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('drivers.field.phone')} required>
            <Input dir="ltr" type="tel" value={form.phone ?? ''} onChange={(e) => update('phone', e.target.value)} placeholder="+966500000000" className="font-mono" />
          </Field>
          <Field label={t('drivers.field.whatsapp')}>
            <Input dir="ltr" type="tel" value={form.whatsapp ?? ''} onChange={(e) => update('whatsapp', e.target.value)} placeholder="+966500000000" className="font-mono" />
          </Field>
          <Field label={t('drivers.field.emergencyContactName')}>
            <Input value={form.emergencyContactName ?? ''} onChange={(e) => update('emergencyContactName', e.target.value)} />
          </Field>
          <Field label={t('drivers.field.emergencyContactPhone')}>
            <Input dir="ltr" type="tel" value={form.emergencyContactPhone ?? ''} onChange={(e) => update('emergencyContactPhone', e.target.value)} placeholder="+923001234567" className="font-mono" />
          </Field>
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.personal')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('drivers.field.dateOfBirth')}>
            <Input type="date" value={form.dateOfBirth ?? ''} onChange={(e) => update('dateOfBirth', e.target.value)} />
          </Field>
          <Field label={t('drivers.field.bloodType')}>
            <Select value={form.bloodType ?? ''} onChange={(v) => update('bloodType', (v || null) as BloodType | null)} placeholder="—">
              {BLOOD_TYPES.map((bt) => <option key={bt} value={bt}>{bt}</option>)}
            </Select>
          </Field>
          <Field label={t('drivers.field.countryOfOrigin')}>
            <Input dir="ltr" value={form.countryOfOrigin ?? ''} onChange={(e) => update('countryOfOrigin', e.target.value.toUpperCase())} placeholder="PK, IN, BD, SA..." className="font-mono uppercase" maxLength={2} />
          </Field>
        </div>
        <LanguagesSelector value={form.languages ?? []} onChange={(v) => update('languages', v)} />
      </fieldset>
    </div>
  );

  const DocumentsTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.iqama')}
        </legend>
        <IqamaInput value={form.iqamaNumber ?? ''} onChange={(v) => update('iqamaNumber', v)} required />
        <Field label={t('drivers.field.iqamaExpiry')} required suffix={<ExpiryIndicator dateString={form.iqamaExpiry || null} />}>
          <Input type="date" value={form.iqamaExpiry ?? ''} onChange={(e) => update('iqamaExpiry', e.target.value)} />
        </Field>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.license')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('drivers.field.licenseNumber')} required>
            <Input dir="ltr" value={form.licenseNumber ?? ''} onChange={(e) => update('licenseNumber', e.target.value)} className="font-mono" />
          </Field>
          <Field label={t('drivers.field.licenseCategory')} required>
            <Select value={form.licenseCategory} onChange={(v) => update('licenseCategory', v as LicenseCategory)}>
              {LICENSE_CATEGORIES.map((lc) => (
                <option key={lc} value={lc}>{t(`drivers.licenseCategory.${lc}`)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('drivers.field.licenseIssueDate')}>
            <Input type="date" value={form.licenseIssueDate ?? ''} onChange={(e) => update('licenseIssueDate', e.target.value)} />
          </Field>
          <Field label={t('drivers.field.licenseExpiry')} required suffix={<ExpiryIndicator dateString={form.licenseExpiry || null} />}>
            <Input type="date" value={form.licenseExpiry ?? ''} onChange={(e) => update('licenseExpiry', e.target.value)} />
          </Field>
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.passport')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('drivers.field.passportNumber')}>
            <Input dir="ltr" value={form.passportNumber ?? ''} onChange={(e) => update('passportNumber', e.target.value)} className="font-mono" />
          </Field>
          <Field label={t('drivers.field.passportCountry')}>
            <Input dir="ltr" value={form.passportCountry ?? ''} onChange={(e) => update('passportCountry', e.target.value.toUpperCase())} placeholder="PK" maxLength={2} className="font-mono uppercase" />
          </Field>
          <Field label={t('drivers.field.passportExpiry')} suffix={<ExpiryIndicator dateString={form.passportExpiry || null} />}>
            <Input type="date" value={form.passportExpiry ?? ''} onChange={(e) => update('passportExpiry', e.target.value)} />
          </Field>
        </div>
      </fieldset>

      {/* PR-DOCVAULT JUN03 — physical document files */}
      {initial?.id && (
        <fieldset className="space-y-3">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            Document files
          </legend>
          <EntityDocumentsPanel scope="driver" entityId={initial.id} slots={DRIVER_SLOTS} />
        </fieldset>
      )}

      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.medical')}
        </legend>
        <Field label={t('drivers.field.medicalCertExpiry')} suffix={<ExpiryIndicator dateString={form.medicalCertExpiry || null} />}>
          <Input type="date" value={form.medicalCertExpiry ?? ''} onChange={(e) => update('medicalCertExpiry', e.target.value)} />
        </Field>
      </fieldset>
    </div>
  );

  const EmploymentTab = (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.employment')}
        </legend>
        <Field label={t('drivers.field.legalEntity')} required>
          <Select value={form.legalEntityId ?? ''} onChange={(v) => update('legalEntityId', v)} placeholder={t('drivers.placeholder.selectLegalEntity')}>
            {legalEntities.map((le) => (
              <option key={le.id} value={le.id}>{le.code} — {le.nameEn}</option>
            ))}
          </Select>
        </Field>
        {/* PR MAY10 P4B-POLISH (A.6) — operational project */}
        <Field label={t('drivers.field.project', { defaultValue: 'Operational project' })}>
          <Select value={form.projectId ?? ''} onChange={(v) => update('projectId', v || null)} placeholder={t('drivers.placeholder.selectProject', { defaultValue: '— No project —' })}>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.code} — {p.name_en}</option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('drivers.field.hireDate')}>
            <Input type="date" value={form.hireDate ?? ''} onChange={(e) => update('hireDate', e.target.value)} />
          </Field>
          <Field label={t('drivers.field.endDate')}>
            <Input type="date" value={form.endDate ?? ''} onChange={(e) => update('endDate', e.target.value)} />
            <p className="text-[11px] text-muted-foreground mt-1">{t('drivers.help.endDateOptional')}</p>
          </Field>
          <Field label={t('drivers.field.status')}>
            <Select value={form.status ?? 'active'} onChange={(v) => update('status', v as DriverStatus)}>
              {STATUSES.map((s) => <option key={s} value={s}>{t(`drivers.status.${s}`)}</option>)}
            </Select>
          </Field>
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.pay')}
        </legend>
        <PayModelSelector value={form.payModel ?? 'fixed_only'} onChange={(v) => update('payModel', v)} />

        {/* PR-F1 MAY 11 — v2 pay model used by salary preview and trip rate auto-fill. */}
        <Field label={t('drivers.field.payModelV2', { defaultValue: 'Pay model (salary calc v2)' })}>
          <select
            value={(form as { payModelV2?: string }).payModelV2 ?? ''}
            onChange={(e) => update('payModelV2' as keyof typeof form, (e.target.value || undefined) as never)}
            className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base"
          >
            <option value="">— use legacy pay_model —</option>
            <option value="fixed_monthly">Fixed monthly</option>
            <option value="base_plus_per_trip">Base + per-trip</option>
          </select>
          <p className="text-[11px] text-muted-foreground mt-1">
            {t('drivers.help.payModelV2', { defaultValue: 'Salary runs use this if set. base_plus_per_trip adds driver_pay per completed trip on top of base.' })}
          </p>
        </Field>

        <Field label={t('drivers.field.baseSalarySar')}>
          <Input
            type="number" step="0.01" min={0} dir="ltr"
            value={form.baseSalarySar ?? 0}
            onChange={(e) => update('baseSalarySar', e.target.value ? Number(e.target.value) : 0)}
            placeholder="2500"
            className="font-mono"
          />
        </Field>

        <Field label={t('drivers.field.baseSalaryV2Sar', { defaultValue: 'Base salary v2 (used by salary preview)' })}>
          <Input
            type="number" step="0.01" min={0} dir="ltr"
            value={(form as { baseSalaryV2Sar?: number }).baseSalaryV2Sar ?? ''}
            onChange={(e) => update('baseSalaryV2Sar' as keyof typeof form, (e.target.value ? Number(e.target.value) : null) as never)}
            placeholder="Leave blank to mirror legacy base"
            className="font-mono"
          />
        </Field>

        {form.payModel === 'target_with_bonus' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-md border border-amber-500/30 bg-amber-500/5">
            <Field label={t('drivers.field.monthlyTripTarget')} required>
              <Input
                type="number" min={1} dir="ltr"
                value={form.monthlyTripTarget ?? ''}
                onChange={(e) => update('monthlyTripTarget', e.target.value ? Number(e.target.value) : null)}
                placeholder="50"
                className="font-mono"
              />
            </Field>
            <Field label={t('drivers.field.bonusPerTripSar')} required>
              <Input
                type="number" step="0.01" min={0.01} dir="ltr"
                value={form.bonusPerTripSar ?? ''}
                onChange={(e) => update('bonusPerTripSar', e.target.value ? Number(e.target.value) : null)}
                placeholder="30"
                className="font-mono"
              />
            </Field>
          </div>
        )}

        {form.payModel === 'custom' && (
          <Field label={t('drivers.field.customPayNotes')}>
            <textarea
              value={form.customPayNotes ?? ''}
              onChange={(e) => update('customPayNotes', e.target.value)}
              rows={3}
              placeholder={t('drivers.placeholder.customPayExample')}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <p className="text-[11px] text-muted-foreground mt-1">{t('drivers.help.customPay')}</p>
          </Field>
        )}

        <PayPreview
          payModel={form.payModel ?? 'fixed_only'}
          baseSalary={Number(form.baseSalarySar) || 0}
          monthlyTarget={form.monthlyTripTarget}
          bonusPerTrip={form.bonusPerTripSar}
        />
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
          {t('drivers.section.banking')}
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={t('drivers.field.bankName')}>
            <Input value={form.bankName ?? ''} onChange={(e) => update('bankName', e.target.value)} placeholder="Al Rajhi, NCB..." />
          </Field>
          <Field label={t('drivers.field.accountHolder')}>
            <Input value={form.accountHolder ?? ''} onChange={(e) => update('accountHolder', e.target.value)} />
          </Field>
          <Field label={t('drivers.field.iban')} className="md:col-span-2">
            <Input dir="ltr" value={form.iban ?? ''} onChange={(e) => update('iban', e.target.value.toUpperCase())} placeholder="SA0000000000000000000000" className="font-mono uppercase" />
          </Field>
        </div>
      </fieldset>

      {initial?.currentVehicles && initial.currentVehicles.length > 0 && (
        <fieldset className="space-y-3 pt-3 border-t border-border">
          <legend className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {t('drivers.section.currentlyDriving')}
          </legend>
          <div className="space-y-2">
            {initial.currentVehicles.map((v) => (
              <div key={v.id} className="rounded-md border border-border bg-muted/20 p-3 flex items-center gap-3">
                <Truck className="h-5 w-5 text-gold shrink-0" />
                <div className="flex-1">
                  <div className="font-mono text-sm font-medium" dir="ltr">{v.plateEn}</div>
                  <div className="text-xs text-muted-foreground">
                    {v.code} · {v.brand} {v.model} · {t(`vehicles.type.${v.vehicleType}`)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </fieldset>
      )}

      <Field label={t('drivers.field.notes')}>
        <textarea
          value={form.notes ?? ''}
          onChange={(e) => update('notes', e.target.value)}
          rows={3}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </Field>
    </div>
  );

  const tabs = [
    {
      key: 'identity',
      label: <span className="inline-flex items-center gap-1.5"><User className="h-4 w-4" /> {t('drivers.tab.identity')}</span>,
      content: IdentityTab,
    },
    {
      key: 'documents',
      label: <span className="inline-flex items-center gap-1.5"><FileCheck className="h-4 w-4" /> {t('drivers.tab.documents')}</span>,
      badge: !iqamaValidation.valid && form.iqamaNumber ? <span className="text-[10px] text-destructive">●</span> : null,
      content: DocumentsTab,
    },
    {
      key: 'employment',
      label: <span className="inline-flex items-center gap-1.5"><Briefcase className="h-4 w-4" /> {t('drivers.tab.employment')}</span>,
      content: EmploymentTab,
    },
    ...(initial?.id ? [{
      key: 'ledger',
      label: <span className="inline-flex items-center gap-1.5"><BookOpen className="h-4 w-4" /> Ledger</span>,
      content: (
        <div className="p-6">
          <LedgerView subjectKind="driver" subjectId={initial.id} />
        </div>
      ),
    }] : []),
  ];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <Tabs tabs={tabs} defaultKey="identity" />

      <div className="shrink-0 border-t border-border bg-muted/30">
        {error && (
          <p className="text-sm text-destructive p-3 mx-6 my-3 rounded bg-destructive/10 border border-destructive/30 flex items-start gap-2" role="alert">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </p>
        )}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-6">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
          )}
          <Button type="submit" variant="gold" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitLabel ?? t('common.save')}
          </Button>
        </div>
      </div>
    </form>
  );
}

function Field({
  label, required, className, children, suffix,
}: {
  label: string; required?: boolean; className?: string; children: React.ReactNode; suffix?: React.ReactNode;
}) {
  return (
    <div className={`space-y-1.5 ${className ?? ''}`}>
      <div className="flex items-center justify-between">
        <Label>{label} {required && <span className="text-destructive">*</span>}</Label>
        {suffix}
      </div>
      {children}
    </div>
  );
}

function Select({
  value, onChange, children, placeholder,
}: {
  value: string; onChange: (v: string) => void; children: React.ReactNode; placeholder?: string;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {children}
    </select>
  );
}
